import React, { useEffect, useState } from 'react';
import { getUserCases, getAppUser } from '../../functions/utils';
import {
  getAuditfileImports,
  deleteAuditfileImport,
  handleAuditfileImport,
  handleAuditfileFinal,
  resetAuditfileStatus,
} from '../../functions/auditfile';
import '../../css/ImportComponent.css';
import ImportHeaderComponent from '../page/ImportHeaderComponent';
import DropdownComponent from '../utilities/DropdownComponent';
import LoaderComponent from '../utilities/LoaderComponent';
import FileDropComponent from '../utilities/FileDropComponent';
import ConfirmationComponent from '../utilities/ConfirmationComponent';
import AuditfileLogo from '../../images/AuditfileLogo.png';
import { useTranslations } from '../utilities/TranslationContext';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faXmark);

const AuditfileComponent = () => {
  const { getTranslation, language } = useTranslations();

  const [userCases, setUserCases] = useState([]);
  const [imports, setImports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCase, setSelectedCase] = useState(false);
  const [periodMode, setPeriodMode] = useState('default');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesMetadata, setPendingFilesMetadata] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [fileDropKey, setFileDropKey] = useState(0);

  const [confirmDeleteId, setConfirmDeleteId] = useState(false);

  const showUploadForm = pendingFiles.length > 0;
  const canImport = showUploadForm && selectedCase && periodMode && !isUploading;

  useEffect(() => { init(); }, []);

  async function init() {
    const appUser = await getAppUser();
    if (!appUser) return setTimeout(init, 300);
    const cases = await getUserCases();
    if (cases) setUserCases(cases);
    const imps = await getAuditfileImports(cases);
    setImports(Array.isArray(imps) ? imps : []);
    setIsLoading(false);
  }

  async function reload() {
    const imps = await getAuditfileImports(userCases);
    setImports(Array.isArray(imps) ? imps : []);
  }

  // ── File handling ──────────────────────────────────────────────────────────────

  function extractFileMetadata(xml) {
    const companyMatch = xml.match(/<Name>([^<]+)<\/Name>/i);
    const fiscalYearMatch = xml.match(/<FiscalYear>([^<]+)<\/FiscalYear>/i);
    const softwareMatch = xml.match(/<SoftwareDesc>([^<]+)<\/SoftwareDesc>/i);
    const yearStartMatch = xml.match(/<YearStart>([^<]+)<\/YearStart>/i);
    const versionMatch = xml.match(/<AuditFileVersion>([^<]+)<\/AuditFileVersion>/i);
    return {
      company: companyMatch ? companyMatch[1].trim() : '',
      fiscalYear: fiscalYearMatch ? fiscalYearMatch[1].trim() : (yearStartMatch ? yearStartMatch[1].trim() : ''),
      software: softwareMatch ? softwareMatch[1].trim() : '',
      version: versionMatch ? versionMatch[1].trim() : '',
    };
  }

  async function onFilesDropped(newFiles) {
    if (newFiles.length === 0) return;
    const metaArr = [];
    for (const file of newFiles) {
      try {
        const xml = await file.text();
        metaArr.push(extractFileMetadata(xml));
      } catch {
        metaArr.push({ company: '', fiscalYear: '', software: '' });
      }
    }
    setPendingFiles(prev => [...prev, ...newFiles]);
    setPendingFilesMetadata(prev => [...prev, ...metaArr]);
  }

  function cancelUpload() {
    setPendingFiles([]);
    setPendingFilesMetadata([]);
    setSelectedCase(false);
    setPeriodMode('default');
    setDropdownKey(k => k + 1);
    setFileDropKey(k => k + 1);
  }

  function removePendingFile(idx) {
    const files = pendingFiles.filter((_, i) => i !== idx);
    const meta = pendingFilesMetadata.filter((_, i) => i !== idx);
    setPendingFiles(files);
    setPendingFilesMetadata(meta);
    setFileDropKey(k => k + 1);
    if (files.length === 0) setDropdownKey(k => k + 1);
  }

  // ── Import ─────────────────────────────────────────────────────────────────────

  async function runImport() {
    if (!selectedCase) return;
    setIsUploading(true);
    await resetAuditfileStatus();

    const entiteit = `${selectedCase}_1`;
    const impidSet = new Map();

    for (const file of pendingFiles) {
      try {
        const result = await handleAuditfileImport({ entiteit, fileName: file.name, file, periodMode });
        if (result?.configId) {
          impidSet.set(result.impid, { impid: result.impid, entiteit });
        }
      } catch (e) {
        console.error('Auditfile import error:', e);
      }
    }

    const finalEntries = Array.from(impidSet.values());
    if (finalEntries.length > 0) {
      try { await handleAuditfileFinal(finalEntries); } catch (e) {}
    }

    setIsUploading(false);
    cancelUpload();
    await reload();
  }

  // ── Delete ─────────────────────────────────────────────────────────────────────

  async function handleDelete(id) {
    await deleteAuditfileImport(id);
    setConfirmDeleteId(false);
    await reload();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  function getCaseName(entiteit) {
    const id = entiteit.substring(0, entiteit.indexOf('_'));
    const c = userCases.find(uc => String(uc.id) === String(id));
    return c ? c.name : entiteit;
  }

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatStamp(stamp) {
    if (!stamp) return '-';
    return new Date(stamp).toLocaleString(`${language?.language_code}-${language?.country_code}`, {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
  }

  return (
    <div className="import-container">
      <ImportHeaderComponent
        title="Auditfile"
        logo={AuditfileLogo}
        subtitle={getTranslation('import_AuditfileSubtitle')}
      />
      <div className="import-component-container">
        <div className="import-component-content">
          <div className="import-content">

            {/* ── Upload section – always visible ── */}
            <div className="import-create-basic">
              <FileDropComponent key={fileDropKey} onFilesAccepted={onFilesDropped} showFileList={false} />
            </div>

            {/* ── Toggled inputs – only when a file is dropped ── */}
            {showUploadForm && !isUploading && (
              <div className="import-create-basic">
                <div className="case-content">
                  <p>{getTranslation('import_FVCase')}*</p>
                  <DropdownComponent
                    key={dropdownKey}
                    options={userCases.map(c => ({ label: c.name, value: c.id, type: 'case' }))}
                    defaultValue={{ label: getTranslation('import_SelectCase'), value: null, type: null }}
                    onSelect={o => o.type === 'case' && setSelectedCase(o.value)}
                    className="case"
                  />
                </div>
                <div className="case-content">
                  <p>Periodemethode*</p>
                  <DropdownComponent
                    key={`pm-${dropdownKey}`}
                    options={[
                      { label: 'Standaard periode', value: 'default', type: 'periodmode' },
                      { label: 'Effectieve datum', value: 'effectivedate', type: 'periodmode' },
                    ]}
                    defaultValue={{ label: 'Standaard periode', value: 'default', type: 'periodmode' }}
                    onSelect={o => o.type === 'periodmode' && setPeriodMode(o.value)}
                    className="periodmode"
                  />
                </div>
              </div>
            )}

            {/* ── Pending files list ── */}
            {showUploadForm && !isUploading && pendingFiles.length > 0 && (
              <div className="import-results-wrapper">
                <table className="import-results-table">
                  <thead>
                    <tr>
                      <th>Bestandsnaam</th>
                      <th>Versie</th>
                      <th>Boekjaar</th>
                      <th>Bedrijf</th>
                      <th>Software</th>
                      <th>Grootte</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingFiles.map((f, i) => {
                      const meta = pendingFilesMetadata[i] || {};
                      return (
                        <tr key={i} className="file-row-pending">
                          <td>{f.name}</td>
                          <td>{meta.version || '-'}</td>
                          <td>{meta.fiscalYear || '-'}</td>
                          <td>{meta.company || '-'}</td>
                          <td>{meta.software || '-'}</td>
                          <td>{formatSize(f.size)}</td>
                          <td>
                            <button className="file-drop-item-remove" onClick={() => removePendingFile(i)} type="button">
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Upload loader ── */}
            {isUploading && <LoaderComponent isLoading paddingTop="10px" />}

            {/* ── Menubar ── */}
            {showUploadForm && !isUploading && (
              <div className="import-menubar">
                <button className={`import-btn submit-btn hover${!canImport ? ' disabled' : ''}`} disabled={!canImport} onClick={runImport}>
                  {getTranslation('import_Import')}
                </button>
                <button className="import-btn cancel-btn hover" onClick={cancelUpload}>
                  {getTranslation('import_Cancel')}
                </button>
              </div>
            )}

            {/* ── Import list – always visible ── */}
            <LoaderComponent isLoading={isLoading} paddingTop="20px" />
            {!isLoading && imports.length > 0 && (
              <div className="list-content">
                <table border="1">
                  <tbody>
                    <tr className="list-header">
                      <td className="list-case">{getTranslation('import_Case')}</td>
                      <td className="list-year">{getTranslation('import_Year')}</td>
                      <td>Bestandsnaam</td>
                      <td>Versie</td>
                      <td className="list-stamp no-wrap">{getTranslation('import_LastUpdated')}</td>
                      <td className="edit"></td>
                    </tr>
                    {imports.map((imp, index) => (
                      <tr key={imp.id} value={index}>
                        <td>{getCaseName(imp.entiteit)}</td>
                        <td>{imp.year}</td>
                        <td>{imp.auditfile_name || '-'}</td>
                        <td>{imp.version || '-'}</td>
                        <td>{formatStamp(imp.stamp)}</td>
                        <td className="td-editor">
                          <div className="delete-list list-editor-option hover" onClick={() => setConfirmDeleteId(imp.id)}>
                            <FontAwesomeIcon icon={faXmark} />
                          </div>
                          {confirmDeleteId === imp.id && (
                            <ConfirmationComponent
                              message="Weet u zeker dat u deze importconfiguratie wilt verwijderen?"
                              onCancel={() => setConfirmDeleteId(false)}
                              onConfirm={() => handleDelete(imp.id)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditfileComponent;
